/**
 * Scout Lesson Screen
 * Displays lessons_v2 with Scout the Fox mascot
 */

import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { ScoutLessonViewer } from '../components/scout'
import { useCompleteLesson } from '../hooks/useAPI'
import { useAuth } from '../contexts/AuthContext'

export default function ScoutLessonScreen() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const completeLessonMutation = useCompleteLesson()

  // Fetch lesson from API
  const { data, isLoading, error } = useQuery({
    queryKey: ['scout-lesson', slug],
    queryFn: async () => {
      const response = await fetch(
        `/api/v1/lessons/v2/by-slug/${slug}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('surviveit_token')}`
          }
        }
      )

      if (!response.ok) {
        throw new Error('Failed to fetch lesson')
      }

      const result = await response.json()
      return result.lesson
    },
    enabled: !!slug,
  })

  const handleComplete = async (lesson) => {
    try {
      await completeLessonMutation.mutateAsync({
        lessonId: lesson.id,
        score: 100, // Full completion
        timeSpent: lesson.est_minutes * 60 // Convert to seconds
      })

      // Navigate to quiz if available
      if (lesson.quizkai_request) {
        navigate(`/lessons/${slug}/quiz`)
      } else {
        // Navigate back to module
        navigate(`/modules/${lesson.module_id}`)
      }
    } catch (error) {
      console.error('Error completing lesson:', error)
      // Still allow navigation even if completion fails
      navigate(`/modules/${lesson.module_id}`)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-orange-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading lesson...</p>
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-6xl mb-4">😕</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Lesson Not Found</h2>
          <p className="text-gray-600 mb-6">
            We couldn't find this lesson. It may have been removed or the link is incorrect.
          </p>
          <button
            onClick={() => navigate('/home')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Home
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
        </div>
      </div>

      {/* Scout Lesson Viewer */}
      <ScoutLessonViewer
        lesson={data}
        onComplete={handleComplete}
      />
    </div>
  )
}
